/* global globalThis */
import trace from '~/lib/trace';
import {
  useEffect,
  useState,
  useContext,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import {View, ScrollView, FlatList, TextInput, Animated} from 'react-native';
import {Divider} from 'react-native-paper';
import {useNavigation, useIsFocused} from '@react-navigation/native';
import moment from 'moment';
import debounce from 'lodash/debounce';

import sy from '~/styles';
import {
  Text,
  ServiceRequestStatusIcon,
  Pressable,
  FloatingAddButton,
  ServiceRequestCard,
} from '~/components/controls';
import {DealerHeader} from '~/components/shared';
import {
  STATUS_CODES,
  STATUS_DESCRIPTIONS,
  STATUS_ORDER,
  EXPIRED_STATUS,
} from '~/types/statuses';
import {service_request as serviceRequestApi} from '~/api/private';
import UserContext from '~/components/context/UserContext';
import {useDispatch, useSelector} from '~/lib/hooks';
import Format from '~/lib/format';
import {setAppIsLoading, setAppDoneLoading} from '~/actions';
import {OVERVIEW_REQUEST_TYPES} from '~/types/request';

import ChevronLeftIcon from '~/images/md-icons/chevron_left/materialicons/24px.svg';
import SearchIcon from '~/images/md-icons/search/materialicons/24px.svg';
import ExpandLessIcon from '~/images/md-icons/expand_less/materialicons/24px.svg';
import ExpandMoreIcon from '~/images/md-icons/expand_more/materialicons/24px.svg';
import WarningAmberIcon from '~/images/md-icons/warning_amber/materialicons/24px.svg';
import WarningIcon from '~/images/md-icons/warning/materialicons/24px.svg';
import HistoryIcon from '~/images/md-icons/history/materialicons/24px.svg';
import PropTypes from 'prop-types';

const MONTH_HEIGHT = 40;
const REQUEST_HEIGHT = 96;

const getHeaderLabel = ({status, type}) => {
  if (type === OVERVIEW_REQUEST_TYPES.RECENTS) {
    return 'Recent bekeken';
  }

  if (status === EXPIRED_STATUS.EXPIRED_60_DAYS) {
    return 'Ouder dan 60 dagen';
  }

  if (status === EXPIRED_STATUS.EXPIRED_30_DAYS) {
    return 'Ouder dan 30 dagen';
  }

  return STATUS_DESCRIPTIONS[status];
};

const getStatusIcon = (status) => {
  if (status === EXPIRED_STATUS.EXPIRED_60_DAYS) {
    return <WarningIcon fill="#972727" />;
  }

  if (status === EXPIRED_STATUS.EXPIRED_30_DAYS) {
    return <WarningAmberIcon fill="#FF6C00" />;
  }

  return <ServiceRequestStatusIcon status={status} />;
};

const StatusHeader = ({status, type, count, onBack}) => (
  <View style={[sy['flex-row']]}>
    <Pressable style={[sy['p-4']]} onPress={onBack}>
      <ChevronLeftIcon />
    </Pressable>
    <View
      style={[
        sy['flex-1'],
        sy['py-4'],
        sy['items-center'],
        sy['flex-row'],
        sy['gap-2'],
      ]}>
      <Text style={[sy.mediumPlus, {lineHeight: 24}]}>
        {getHeaderLabel({status, type})}
        {count > 0 ? <>&nbsp;&middot;&nbsp;{count}</> : null}
      </Text>
    </View>
    <View style={[sy['p-4']]}>{getStatusIcon(status)}</View>
  </View>
);
StatusHeader.propTypes = {
  status: PropTypes.any,
  type: PropTypes.any,
  count: PropTypes.any,
  onBack: PropTypes.any,
};

const StatusCard = ({status, count, divider, onPress}) => (
  <Pressable onPress={onPress} style={[sy['px-4'], sy['bg-white']]}>
    <View
      style={[
        sy['flex-row'],
        sy['py-4'],
        sy['justify-between'],
        sy['items-center'],
      ]}>
      <View style={[sy['flex-row'], sy['items-center'], sy['gap-4']]}>
        {(() => {
          if (status === OVERVIEW_REQUEST_TYPES.RECENTS) {
            return (
              <>
                <HistoryIcon fill="#4a4a49" />
                <Text style={sy.mediumBold}>Recent bekeken</Text>
              </>
            );
          }

          if (status === EXPIRED_STATUS.EXPIRED_60_DAYS) {
            return (
              <>
                <WarningIcon fill="#972727" />
                <Text style={sy.mediumBold}>Ouder dan 60 dagen</Text>
              </>
            );
          }

          if (status === EXPIRED_STATUS.EXPIRED_30_DAYS) {
            return (
              <>
                <WarningAmberIcon fill="#FF6C00" />
                <Text style={sy.mediumBold}>Ouder dan 30 dagen</Text>
              </>
            );
          }

          return (
            <>
              <ServiceRequestStatusIcon status={status} />
              <Text style={sy.mediumBold}>{STATUS_DESCRIPTIONS[status]}</Text>
            </>
          );
        })()}
      </View>
      {count > 0 && (
        <Text style={[sy['text-lightgray'], sy.smallRegular]}>
          {Format.number(count, 0, true)}
        </Text>
      )}
    </View>
    {divider}
  </Pressable>
);
StatusCard.propTypes = {
  status: PropTypes.any,
  count: PropTypes.any,
  divider: PropTypes.any,
  onPress: PropTypes.any,
};

const DateLabel = ({date}) => (
  <View
    style={[
      sy['flex-row'],
      sy['items-center'],
      sy['px-4'],
      sy['mb-2'],
      {height: MONTH_HEIGHT - 8},
    ]}>
    <Text style={sy.mediumBold}>
      {moment(date, 'DD-MM-YYYY').format('D MMMM YYYY')}
    </Text>
  </View>
);
DateLabel.propTypes = {
  date: PropTypes.any,
};

const MonthLabel = ({date}) => (
  <View
    style={[
      sy['flex-row'],
      sy['items-center'],
      sy['px-4'],
      sy['mb-2'],
      {height: MONTH_HEIGHT - 8},
    ]}>
    <Text style={sy.mediumBold}>
      {moment(date, 'YYYY-MM').format('MMMM YYYY')}
    </Text>
  </View>
);
MonthLabel.propTypes = {
  date: PropTypes.any,
};

const Grouping = ({header, count, children}) => {
  const [expand, setExpand] = useState(true);

  return (
    <View style={[sy['bg-white'], sy['overflow-hidden'], sy['rounded-lg']]}>
      <Pressable
        style={[
          sy['flex-row'],
          sy['items-center'],
          sy['justify-between'],
          sy['p-4'],
          sy['gap-4'],
          sy['bg-white'],
        ]}
        onPress={() => setExpand(!expand)}>
        <View style={[sy['flex-row'], sy['items-center'], sy['gap-4']]}>
          {expand ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          <Text style={[sy.mediumPlus, sy.truncate]}>{header}</Text>
        </View>
        <Text style={[sy['text-lightgray'], sy.smallRegular]}>
          {Format.number(count, 0, true)}
        </Text>
      </Pressable>
      <Divider style={sy['bg-gray95']} />
      {expand && <>{children}</>}
    </View>
  );
};
Grouping.propTypes = {
  header: PropTypes.any,
  count: PropTypes.any,
  children: PropTypes.any,
};

const withoutCancelled = (statuses) =>
  Object.fromEntries(
    Object.entries(statuses ?? {}).filter(
      ([status]) => status !== STATUS_CODES.Cancelled,
    ),
  );

const List = ({overview}) => {
  const navigation = useNavigation();
  const user_settings = useSelector((state) => state.user_settings);

  const [recents, setRecents] = useState(null);

  useEffect(() => {
    if (user_settings?.is_super_user) {
      const storedRecents =
        globalThis.window?.sessionStorage.getItem('recents');
      if (storedRecents) {
        setRecents(JSON.parse(storedRecents));
      }
    }
  }, [user_settings]);

  const requests = withoutCancelled(overview[OVERVIEW_REQUEST_TYPES.REQUEST]);
  const replacements = withoutCancelled(
    overview[OVERVIEW_REQUEST_TYPES.REPLACEMENT_VEHICLE],
  );
  const damages = withoutCancelled(overview[OVERVIEW_REQUEST_TYPES.DAMAGE]);

  const request_count = Object.values(requests).reduce(
    (acc, count) => acc + count,
    0,
  );

  const replacement_count = Object.values(replacements).reduce(
    (acc, count) => acc + count,
    0,
  );

  const damage_count = Object.values(damages).reduce(
    (acc, count) => acc + count,
    0,
  );

  let expiredStatus = null;
  if (
    overview[OVERVIEW_REQUEST_TYPES.EXPIRED]?.[EXPIRED_STATUS.EXPIRED_60_DAYS]
  ) {
    expiredStatus = EXPIRED_STATUS.EXPIRED_60_DAYS;
  } else if (
    overview[OVERVIEW_REQUEST_TYPES.EXPIRED]?.[EXPIRED_STATUS.EXPIRED_30_DAYS]
  ) {
    expiredStatus = EXPIRED_STATUS.EXPIRED_30_DAYS;
  }

  return (
    <ScrollView
      style={[sy['flex-1'], sy['bg-gray95'], sy['p-2']]}
      contentContainerStyle={[sy['gap-2'], sy['mb-22']]}>
      {recents && recents.length > 0 && (
        <View style={[sy['bg-white'], sy['overflow-hidden'], sy['rounded-lg']]}>
          <StatusCard
            status={OVERVIEW_REQUEST_TYPES.RECENTS}
            count={recents.length}
            onPress={() =>
              navigation.push('Tabs', {
                screen: 'Search',
                params: {
                  type: OVERVIEW_REQUEST_TYPES.RECENTS,
                },
              })
            }
          />
        </View>
      )}

      {expiredStatus ? (
        <View style={[sy['bg-white'], sy['overflow-hidden'], sy['rounded-lg']]}>
          <StatusCard
            status={expiredStatus}
            count={overview[OVERVIEW_REQUEST_TYPES.EXPIRED][expiredStatus]}
            onPress={() =>
              navigation.push('Tabs', {
                screen: 'Search',
                params: {
                  status: expiredStatus,
                  type: OVERVIEW_REQUEST_TYPES.EXPIRED,
                },
              })
            }
          />
        </View>
      ) : null}
      <Grouping header="Werkopdracht" count={request_count}>
        {Object.keys(requests)
          .sort((a, b) => STATUS_ORDER.indexOf(a) - STATUS_ORDER.indexOf(b))
          .map((status, index, array) => (
            <StatusCard
              key={status}
              status={status}
              count={requests[status]}
              divider={
                index >= array.length - 1 ? null : (
                  <Divider style={sy['bg-gray95']} />
                )
              }
              onPress={() =>
                navigation.push('Tabs', {
                  screen: 'Search',
                  params: {status, type: OVERVIEW_REQUEST_TYPES.REQUEST},
                })
              }
            />
          ))}
      </Grouping>
      <Grouping header="Vervangende auto" count={replacement_count}>
        {Object.keys(replacements)
          .sort((a, b) => STATUS_ORDER.indexOf(a) - STATUS_ORDER.indexOf(b))
          .map((status, index, array) => (
            <StatusCard
              key={status}
              status={status}
              count={replacements[status]}
              divider={
                index >= array.length - 1 ? null : (
                  <Divider style={sy['bg-gray95']} />
                )
              }
              onPress={() =>
                navigation.push('Tabs', {
                  screen: 'Search',
                  params: {
                    status,
                    type: OVERVIEW_REQUEST_TYPES.REPLACEMENT_VEHICLE,
                  },
                })
              }
            />
          ))}
      </Grouping>
      <Grouping header="Schadewerkopdracht" count={damage_count}>
        {Object.keys(damages)
          .sort((a, b) => STATUS_ORDER.indexOf(a) - STATUS_ORDER.indexOf(b))
          .map((status, index, array) => (
            <StatusCard
              key={status}
              status={status}
              count={damages[status]}
              divider={
                index >= array.length - 1 ? null : (
                  <Divider style={sy['bg-gray95']} />
                )
              }
              onPress={() =>
                navigation.push('Tabs', {
                  screen: 'Search',
                  params: {status, type: OVERVIEW_REQUEST_TYPES.DAMAGE},
                })
              }
            />
          ))}
      </Grouping>
    </ScrollView>
  );
};
List.propTypes = {
  overview: PropTypes.any,
};

const doSearch = debounce(
  async ({prefix, selectedType, selectedStatus}, setSelected, dispatch) => {
    try {
      let data;
      if (prefix?.length > 2) {
        ({result: data} = await serviceRequestApi.search({
          prefix,
        }));
      } else if (selectedType === OVERVIEW_REQUEST_TYPES.RECENTS) {
        const recents = JSON.parse(
          globalThis.window?.sessionStorage.getItem('recents') ?? '[]',
        );

        ({result: data} = await serviceRequestApi.search({
          recents,
        }));
      } else if (selectedStatus) {
        ({result: data} = await serviceRequestApi.search({
          selectedType,
          selectedStatus,
        }));
      }

      if (data) {
        setSelected(data);
      } else {
        setSelected(null);
      }
    } catch {}

    dispatch(setAppDoneLoading());
  },
  200,
);

const Search = ({navigation, route}) => {
  const isFocused = useIsFocused();
  const dispatch = useDispatch();

  const selectedType = route.params?.type;
  const selectedStatus = route.params?.status;

  const [overview, setOverview] = useState(null);
  const [selected, setSelected] = useState(null);
  const [prefix, setPrefix] = useState('');

  const {me} = useContext(UserContext);

  const forceFetch = useSelector((state) => state.serviceRequestInsert);
  const forceRefresh = useSelector((state) => state.serviceRequestUpdate);

  const cancelAnim = useRef(new Animated.Value(0)).current;

  // Precompute every row's height/offset once per result set. Doing this
  // inline in getItemLayout re-walks the preceding rows on every call, which
  // makes laying out the list O(n^2).
  const itemLayouts = useMemo(() => {
    const layouts = [];
    let offset = 0;

    for (let index = 0; index < (selected?.length ?? 0); index++) {
      const length =
        selected[index].type === 'month' ? MONTH_HEIGHT : REQUEST_HEIGHT;

      layouts.push({length, offset, index});
      offset += length;
    }

    return layouts;
  }, [selected]);

  const getItemLayout = useCallback(
    (data, index) => itemLayouts[index],
    [itemLayouts],
  );

  const keyExtractor = useCallback(
    (item) =>
      item.type === 'request'
        ? `request-${item.value.request.id}`
        : `${item.type}-${item.value}`,
    [],
  );

  const renderItem = useCallback(
    ({item}) => {
      if (item.type === 'date') {
        return <DateLabel date={item.value} />;
      }

      if (item.type === 'month') {
        return <MonthLabel date={item.value} />;
      }

      if (item.type === 'request') {
        return (
          <ServiceRequestCard
            style={[sy['mb-2'], {height: REQUEST_HEIGHT - 8}]}
            show_time={selectedType === OVERVIEW_REQUEST_TYPES.RECENTS}
            {...item.value.request}
          />
        );
      }

      return null;
    },
    [selectedType],
  );

  useEffect(() => {
    const unsubscribe = navigation.addListener('tabPress', () => {
      if (isFocused) {
        if (navigation.getParent()?.canGoBack()) {
          navigation.popToTop();
        } else {
          setPrefix('');
          setSelected(null);
        }
      }
    });

    return unsubscribe;
  }, [isFocused, navigation]);

  useEffect(() => {
    if (prefix.length) {
      Animated.timing(cancelAnim, {
        toValue: 100,
        duration: 250,
        useNativeDriver: true,
      }).start();
    } else {
      Animated.timing(cancelAnim, {
        toValue: 0,
        duration: 0,
        useNativeDriver: true,
      }).start();
    }
  }, [cancelAnim, prefix]);

  useEffect(() => {
    if (!isFocused) {
      return;
    }

    dispatch(setAppIsLoading());

    return () => {
      dispatch(setAppDoneLoading());
    };
  }, [isFocused, dispatch]);

  useEffect(() => {
    if (selectedStatus) {
      return;
    }

    const fetch = async () => {
      const result = await serviceRequestApi.overview();

      dispatch(setAppDoneLoading());

      if (!result) {
        return;
      }

      const {result: overview} = result;
      setOverview(overview);
    };

    if (isFocused) {
      fetch().catch(trace);
    }
  }, [
    isFocused,
    me,
    selectedStatus,
    forceFetch,
    forceRefresh,
    dispatch,
    setOverview,
  ]);

  useEffect(() => {
    if (!isFocused) {
      return;
    }

    doSearch({prefix, selectedType, selectedStatus}, setSelected, dispatch);
  }, [isFocused, prefix, selectedStatus, selectedType]);

  return (
    <View style={sy.mainView}>
      {selectedStatus || selectedType ? (
        <StatusHeader
          status={selectedStatus}
          type={selectedType}
          count={selected?.filter((item) => item.type === 'request').length}
          onBack={() => navigation.navigate('Search')}
        />
      ) : (
        <View style={[sy['px-4'], sy['pt-4'], sy['pb-3']]}>
          <DealerHeader
            titleStyle={{textTransform: 'capitalize'}}
            title="Opdrachten"
          />

          <View style={[sy['flex-row'], sy['items-center']]}>
            <View
              style={[
                sy['flex-row'],
                sy['flex-1'],
                sy['p-2'],
                sy['mt-2'],
                sy['items-center'],
                {
                  borderRadius: 10,
                  backgroundColor: '#EBEBEB',
                },
              ]}>
              <SearchIcon
                fill={prefix.length ? '#4A4A49' : '#828282'}
                width={18}
                height={18}
              />
              <TextInput
                style={[
                  sy.regular,
                  sy['flex-1'],
                  {
                    color: prefix?.length > 0 ? '#4A4A49' : '#828282',
                    marginLeft: 28,
                  },
                ]}
                placeholder={'Bahnkick'}
                value={prefix}
                onChangeText={setPrefix}
              />
            </View>
            <Animated.View
              style={[
                sy['mt-2'],
                {
                  height: 29,
                  paddingLeft: prefix.length ? 8 : 0,
                  maxWidth: cancelAnim,
                  justifyContent: 'center',
                },
              ]}>
              <Pressable onPress={() => setPrefix('')}>
                <Text
                  style={[
                    sy.regular,
                    {
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                    },
                  ]}>
                  Annuleren
                </Text>
              </Pressable>
            </Animated.View>
          </View>
        </View>
      )}
      <Divider style={sy['bg-gray88']} />

      {selected ? (
        <FlatList
          style={[sy['bg-gray95'], sy['px-2'], sy['pt-2']]}
          data={selected}
          renderItem={renderItem}
          keyExtractor={keyExtractor}
          initialNumToRender={10}
          getItemLayout={getItemLayout}
        />
      ) : (
        overview && (
          <>
            <List overview={overview} />
            <FloatingAddButton />
          </>
        )
      )}
    </View>
  );
};
Search.propTypes = {
  navigation: PropTypes.any,
  route: PropTypes.any,
};

export default Search;
